import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import AppShell from './layout/AppShell.js';
import LoginPage from './auth/LoginPage.js';
import RegisterPage from './auth/RegisterPage.js';
import RequireAuth from './auth/RequireAuth.js';
import DeckBuilderPage from './pages/DeckBuilderPage.js';
import DeckListPage from './pages/DeckListPage.js';
import GamesPage from './pages/GamesPage.js';
import LeaderboardPage from './pages/LeaderboardPage.js';
import LibraryPage from './pages/LibraryPage.js';
import LobbiesPage from './pages/LobbiesPage.js';
import MatchPage from './pages/MatchPage.js';
import MePage from './pages/MePage.js';
import MyMatchesPage from './pages/MyMatchesPage.js';
import MyTeamsBuilderPage from './pages/MyTeamsBuilderPage.js';
import MyTeamsPage from './pages/MyTeamsPage.js';
import NotFoundPage from './pages/NotFoundPage.js';
import PublicTeamsPage from './pages/PublicTeamsPage.js';
import ReplayPage from './pages/ReplayPage.js';

// Dev-only UI harness for the AFL arena (fabricated match states at any
// viewport). Lazy behind the DEV flag so production never bundles it.
const DevAflUiHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/DevAflUiHarness.js'))
  : null;

// Same idea for the Golden Balls arena: every phase (nomination, the
// vote reveal with a tie-break, a killer landing in the Win row, the
// three-ball final, the debrief) mountable at a phone viewport without
// playing a backend match to reach it.
const DevGoldenBallsUiHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/DevGoldenBallsUiHarness.js'))
  : null;

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<GamesPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/me"
            element={
              <RequireAuth>
                <MePage />
              </RequireAuth>
            }
          />
          <Route
            path="/me/matches"
            element={
              <RequireAuth>
                <MyMatchesPage />
              </RequireAuth>
            }
          />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route
            path="/lobbies"
            element={
              <RequireAuth>
                <LobbiesPage />
              </RequireAuth>
            }
          />
          {/*
            Match page is public — non-participants get a read-only
            spectator view. The MatchPage component itself decides
            what to show based on whether the visitor is signed in
            and whether they're a participant.
          */}
          <Route path="/matches/:matchId" element={<MatchPage />} />
          {/* Replay is also public — anyone with the URL can step
              through the event log of any match. */}
          <Route path="/matches/:matchId/replay" element={<ReplayPage />} />
          <Route
            path="/library/:gameKey"
            element={
              <RequireAuth>
                <LibraryPage />
              </RequireAuth>
            }
          />
          <Route
            path="/decks/:gameKey"
            element={
              <RequireAuth>
                <DeckListPage />
              </RequireAuth>
            }
          />
          <Route
            path="/decks/:gameKey/:deckId"
            element={
              <RequireAuth>
                <DeckBuilderPage />
              </RequireAuth>
            }
          />
          {DevAflUiHarness && (
            <Route
              path="/dev/afl-ui"
              element={
                <Suspense fallback={null}>
                  <DevAflUiHarness />
                </Suspense>
              }
            />
          )}
          {DevGoldenBallsUiHarness && (
            <Route
              path="/dev/goldenballs-ui"
              element={
                <Suspense fallback={null}>
                  <DevGoldenBallsUiHarness />
                </Suspense>
              }
            />
          )}
          {/* My Teams. The builder is addressed by its (sport, format,
              era) cell — the same triple the API uses — so a URL is
              shareable and reload-safe without carrying a lineup id. */}
          <Route
            path="/my-teams"
            element={
              <RequireAuth>
                <MyTeamsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/my-teams/:sport/:format/:era"
            element={
              <RequireAuth>
                <MyTeamsBuilderPage />
              </RequireAuth>
            }
          />
          {/* Public and deliberately unguarded: this is the page
              hbrennan.com links to, and it must render for a visitor
              who has no account at all. */}
          <Route path="/u/:username/teams" element={<PublicTeamsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
